import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
} from "@mui/material";
import { useSessionManager } from "hooks/useSessionManager";
import { useAuth } from "store/AuthProvider";
import { useNavigate } from "react-router-dom";
import { DataConflictModals } from "components/DataConflictModals/DataConflictModals";

/**
 * 会话管理相关的对话框组件
 * 处理 Token 过期、应用崩溃恢复等场景的 UI
 */
export const SessionModals: React.FC = () => {
    const { 
        sessionState, 
        clearTokenExpiredState, 
        acknowledgeUnexpectedExit,
        acknowledgeDataConflict,
    } = useSessionManager();
    const auth = useAuth();
    const navigate = useNavigate();

    // B4: 应用崩溃恢复对话框
    const handleResumeCrashedSession = () => {
        acknowledgeUnexpectedExit();
        // 如果用户已登录，继续操作
        if (auth.isAuth) {
            // 可以导航到同步页面或主页
            navigate("/home");
        } else {
            // 如果未登录，导航到登录页面
            navigate("/signin");
        }
    };

    // B2 & B3: Token 过期对话框 - 重新登录
    const handleReAuth = () => {
        clearTokenExpiredState();
        auth.logout(); // 清除当前会话
        navigate("/signin");
    };

    return (
        <>
            {/* B4: 应用崩溃恢复对话框 */}
            <Dialog
                open={sessionState.hasUnexpectedExit}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight="bold">
                            恢復未同步的數據
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        檢測到應用程序異常退出
                    </Alert>
                    <Typography variant="body1" paragraph>
                        您有 <strong>{sessionState.pendingDataOnStartup}</strong> 個未同步的旗袋來自您之前的連線。
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        點擊下方按鈕以恢復並同步這些數據。
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleResumeCrashedSession}
                        fullWidth
                    >
                        恢復連線並同步
                    </Button>
                </DialogActions>
            </Dialog>

            {/* B2: Token 过期对话框（在线状态） */}
            <Dialog
                open={sessionState.needsReAuth && !sessionState.hasUnexpectedExit}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">
                        連線已逾時
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        您的登入連線已逾時
                    </Alert>
                    <Typography variant="body1" paragraph>
                        如需繼續操作，請重新登入。
                    </Typography>
                    {localStorage.getItem("pendingDataBeforeExpiry") && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            您的待處理數據已安全保存，重新登入後將自動恢復。
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleReAuth}
                        fullWidth
                    >
                        重新登入
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 数据冲突对话框 */}
            <DataConflictModals
                open={sessionState.hasDataConflict}
                conflictDataCount={sessionState.conflictDataCount}
                onClose={acknowledgeDataConflict}
            />
        </>
    );
};

export default SessionModals;

